/**
 * What a change order's revision history reads like (2026-09-22, 0041).
 *
 * A revision is only worth keeping if it can be compared with what replaced
 * it — "Revision 1" on its own says nothing. So this module does two things:
 * describes a superseded version, and says what changed between it and the
 * version that replaced it.
 *
 * Pure: the panel renders what it returns.
 */

import { fmtDate, fmtMoney } from '@/lib/dom';
import { signedMoney } from '@/lib/change-order-ui';
import { changeOrderTotal } from '@/lib/change-order-budget';
import type { ChangeOrderRow, ChangeOrderRevisionRow } from '@/types/database';

/** The parts of a version this module compares — a row or a revision. */
export type VersionFields = Pick<ChangeOrderRow,
  'title' | 'scope' | 'cost_delta_cents' | 'designer_fee_cents' | 'affected_room_ids' | 'affected_spec_ids'>;

export type RevisionFields = Pick<ChangeOrderRevisionRow,
  'revision' | 'title' | 'scope' | 'cost_delta_cents' | 'designer_fee_cents'
  | 'affected_room_ids' | 'affected_spec_ids' | 'acknowledged_at' | 'acknowledged_by_name'
  | 'approved_via' | 'approval_note' | 'superseded_at'>;

/**
 * "Approved by Jane Anderson, Sep 19 · signed in the client portal".
 *
 * Deliberately NOT `approvalLine()` from change-order-ui: that one asks
 * whether the change order is currently approved, and a superseded version
 * never is — it was, which is the whole point of keeping it.
 */
export function revisionApproval(r: Pick<RevisionFields,
  'acknowledged_at' | 'acknowledged_by_name' | 'approved_via' | 'approval_note'>): string {
  const who = r.acknowledged_by_name ? `Approved by ${r.acknowledged_by_name}` : 'Approved';
  const when = r.acknowledged_at ? `, ${fmtDate(r.acknowledged_at)}` : '';
  const how = r.approved_via === 'designer'
    ? ` · recorded by the designer${r.approval_note ? `: ${r.approval_note}` : ''}`
    : r.approved_via === 'portal' ? ' · signed in the client portal' : '';
  return `${who}${when}${how}`;
}

/** "Revision 1 · +$5,500 · superseded Sep 22". */
export function revisionHeadline(r: Pick<RevisionFields, 'revision' | 'cost_delta_cents' | 'designer_fee_cents' | 'superseded_at'>): string {
  const total = signedMoney(changeOrderTotal(r));
  const when = fmtDate(r.superseded_at);
  return `Revision ${r.revision} · ${total}${when === '—' ? '' : ` · superseded ${when}`}`;
}

/**
 * What changed between a superseded version and the one that replaced it, in
 * plain sentences. Empty when nothing did.
 *
 * Money is shown from → to, because "the work changed" is useless when the
 * question is always "by how much". Scope and title are reported as edited
 * rather than diffed: a paragraph diff in a list row is unreadable, and the
 * old text is right there above it.
 */
export function changesBetween(before: VersionFields, after: VersionFields): string[] {
  const out: string[] = [];
  if (before.cost_delta_cents !== after.cost_delta_cents) {
    out.push(`Work ${signedMoney(before.cost_delta_cents)} → ${signedMoney(after.cost_delta_cents)}`);
  }
  if (before.designer_fee_cents !== after.designer_fee_cents) {
    out.push(`Design fee ${signedMoney(before.designer_fee_cents)} → ${signedMoney(after.designer_fee_cents)}`);
  }
  const beforeTotal = changeOrderTotal(before);
  const afterTotal = changeOrderTotal(after);
  if (beforeTotal !== afterTotal) {
    const diff = afterTotal - beforeTotal;
    out.push(`Total ${signedMoney(beforeTotal)} → ${signedMoney(afterTotal)} (${diff < 0 ? '−' : '+'}${fmtMoney(Math.abs(diff))})`);
  }
  if (before.title !== after.title) out.push(`Title “${before.title}” → “${after.title}”`);
  if (before.scope !== after.scope) out.push('Scope edited');
  if (!sameIds(before.affected_room_ids, after.affected_room_ids)) out.push('Rooms changed');
  if (!sameIds(before.affected_spec_ids, after.affected_spec_ids)) out.push('Materials changed');
  return out;
}

/** Order is not meaningful in these arrays — only membership is. */
function sameIds(a: readonly string[] | null, b: readonly string[] | null): boolean {
  const x = [...(a ?? [])].sort();
  const y = [...(b ?? [])].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/**
 * The history for one change order, newest first, each version paired with
 * what replaced it.
 *
 * `current` is the live row: revision N was replaced by revision N+1, and the
 * LAST one was replaced by what's on screen now. Sorting here rather than
 * trusting the query's order — a caller that passed them the other way round
 * would otherwise report every change backwards.
 */
export interface HistoryEntry {
  revision: RevisionFields;
  approval: string;
  headline: string;
  changes: string[];
}

export function buildHistory(revisions: readonly RevisionFields[], current: VersionFields): HistoryEntry[] {
  const ordered = [...revisions].sort((a, b) => a.revision - b.revision);
  return ordered
    .map((rev, i) => ({
      revision: rev,
      approval: revisionApproval(rev),
      headline: revisionHeadline(rev),
      changes: changesBetween(rev, ordered[i + 1] ?? current),
    }))
    .reverse();
}

/** Shown before revising: what it costs to reopen an approved change order. */
export function reviseWarning(co: Pick<ChangeOrderRow, 'number' | 'cost_delta_cents' | 'designer_fee_cents'>): string {
  return `Revise Change Order #${co.number}?\n\n`
    + `The approved version (${signedMoney(changeOrderTotal(co))}) is kept in its history, `
    + `but until you send it and it’s approved again it stops counting toward the revised budget `
    + `and disappears from the client’s portal.`;
}
